import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoriesApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useToast } from '@/hooks/use-toast'

export function useCategories() {
    return useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const response = await categoriesApi.list()
            return response.data.data
        },
    })
}

export function useCategory(id: number | null) {
    return useQuery({
        queryKey: ['categories', id],
        queryFn: async () => {
            if (!id) return null
            const response = await categoriesApi.get(id)
            return response.data.data
        },
        enabled: !!id,
    })
}

export function useCreateCategory() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: categoriesApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast({
                title: 'Success',
                description: 'Category created successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}

export function useUpdateCategory() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: { name: string } }) =>
            categoriesApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast({
                title: 'Success',
                description: 'Category updated successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}

export function useDeleteCategory() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: categoriesApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast({
                title: 'Success',
                description: 'Category deleted successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}
